import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert,
  Chip, Stack, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  CartesianGrid, Legend, BarChart, Bar,
} from 'recharts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BoltIcon from '@mui/icons-material/Bolt';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import { adminApi } from '../../api/adminApi';
import {
  StatCard, ChartCard, areaGradient, trendFromSeries, fmtCompact, RefreshIndicator,
} from '../../components/analytics/AnalyticsKit';

const ALARM_ICON = {
  OK: <CheckCircleIcon color="success" fontSize="small" />,
  ALARM: <ErrorIcon color="error" fontSize="small" />,
  INSUFFICIENT_DATA: <WarningIcon color="warning" fontSize="small" />,
};
const ALARM_COLOR = { OK: 'success', ALARM: 'error', INSUFFICIENT_DATA: 'warning' };

export default function MonitoringPage() {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [window24, setWindow24] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intervalMs, setIntervalMs] = useState(60000);
  const [lastRefreshAt, setLastRefreshAt] = useState(Date.now());

  useEffect(() => {
    let active = true;
    let id;
    const fetchData = () => Promise.all([adminApi.getPlatformHealth(), adminApi.getPlatformMetrics(window24)])
      .then(([h, m]) => { if (active) { setHealth(h); setMetrics(m); setError(null); setLastRefreshAt(Date.now()); } })
      .catch((e) => { if (active) { setError(e.message); } })
      .finally(() => { if (active) { setLoading(false); } });

    fetchData();
    // Poll on the admin-configured monitoring window (data is cached server-side
    // for the same window, so viewers share one set of CloudWatch calls).
    adminApi.getCacheSettings()
      .then((d) => {
        const secs = Math.max(15, Number(d?.config?.monitoringSeconds) || 60);
        if (active) { setIntervalMs(secs * 1000); id = setInterval(fetchData, secs * 1000); }
      })
      .catch(() => { if (active) { setIntervalMs(60000); id = setInterval(fetchData, 60000); } });

    return () => { active = false; if (id) { clearInterval(id); } };
  }, [window24]);

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }

  const alarms = health?.alarms || [];
  const summary = metrics?.summary || {};
  const timeSeries = metrics?.requestsOverTime || [];
  const lambdaMetrics = metrics?.lambdaMetrics || [];

  const inAlarm = alarms.filter((a) => a.state === 'ALARM');
  const allOk = inAlarm.length === 0;
  const reqTrend = trendFromSeries(timeSeries, 'requests');
  const reqSpark = timeSeries.map((r) => ({ v: r.requests }));
  const errSpark = timeSeries.map((r) => ({ v: r.errors }));

  return (
    <Box sx={{ p: 3, maxWidth: 1360 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" rowGap={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={800}>Platform Monitoring</Typography>
          <Chip
            icon={allOk ? <CheckCircleIcon /> : <ErrorIcon />}
            label={allOk ? 'All Systems Healthy' : `${inAlarm.length} Alarm${inAlarm.length > 1 ? 's' : ''} Firing`}
            color={allOk ? 'success' : 'error'}
            sx={{ fontWeight: 700 }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RefreshIndicator intervalMs={intervalMs} lastRefreshAt={lastRefreshAt} />
          <ToggleButtonGroup size="small" exclusive value={window24}
            onChange={(_, v) => { if (v) { setWindow24(v); } }}>
            <ToggleButton value="24h">24h</ToggleButton>
            <ToggleButton value="7d">7 days</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Key metrics */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label={`API Requests (${window24})`} value={fmtCompact(summary.apiRequests24h ?? 0)}
            trend={reqTrend} spark={reqSpark} icon={<BoltIcon />} color="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Error Rate" value={`${summary.errorRate24h ?? 0}%`}
            spark={errSpark} icon={<ErrorOutlineIcon />}
            color={(summary.errorRate24h ?? 0) > 1 ? '#c62828' : '#2e7d32'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Avg Latency" value={summary.avgLatencyMs !== null && summary.avgLatencyMs !== undefined ? `${summary.avgLatencyMs} ms` : '—'}
            icon={<SpeedIcon />} color="#7b1fa2" sub="mean Lambda duration" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Active Tenants" value={summary.activeTenants ?? '—'}
            icon={<GroupsIcon />} color="#00897b" sub={`${summary.totalTenants ?? 0} total`} />
        </Grid>
      </Grid>

      {/* Charts row */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={7}>
          <ChartCard title={`Request Volume & Errors — ${window24}`} subtitle="Lambda invocations (CloudWatch, auto-refreshing)">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={timeSeries}>
                <defs>
                  {areaGradient('mReq', '#1565c0')}
                  {areaGradient('mErr', '#c62828')}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                <ChartTooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="requests" name="Requests" stroke="#1565c0" strokeWidth={2} fill="url(#mReq)" dot={false} />
                <Area type="monotone" dataKey="errors" name="Errors" stroke="#c62828" strokeWidth={2} fill="url(#mErr)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="Slowest Functions" subtitle="p99 duration over the window (ms)">
            {lambdaMetrics.length === 0 ? (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No duration data yet.</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(240, lambdaMetrics.slice(0, 10).length * 28)}>
                <BarChart data={lambdaMetrics.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(0,0,0,.08)" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} tickLine={false} axisLine={false} />
                  <ChartTooltip formatter={(v) => `${Number(v).toLocaleString()} ms`} />
                  <Bar dataKey="p99" name="p99 ms" radius={[0, 5, 5, 0]} maxBarSize={16}
                    fill="#5c6bc0" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Alarms */}
      <ChartCard title="CloudWatch Alarms"
        subtitle={health?.cachedAt ? `refreshed ${new Date(health.cachedAt).toLocaleTimeString()}` : ''}>
        {alarms.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No alarm data available.</Typography>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {[...alarms].sort((a, b) => (a.state === 'ALARM' ? -1 : 1) - (b.state === 'ALARM' ? -1 : 1)).map((alarm) => (
              <Stack key={alarm.name} direction="row" spacing={1.5} alignItems="flex-start" sx={{ px: 1, py: 1.25 }}>
                <Box sx={{ pt: 0.25 }}>{ALARM_ICON[alarm.state] || null}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700}>{alarm.name}</Typography>
                    <Chip label={alarm.state} size="small" color={ALARM_COLOR[alarm.state] || 'default'}
                      variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                    {alarm.metric && (
                      <Typography variant="caption" color="text.disabled">
                        {alarm.metric} {alarm.comparison ? `· threshold ${alarm.threshold}` : ''}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {alarm.description || '—'}
                  </Typography>
                  {alarm.stateReason && (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }} display="block" noWrap>
                      {alarm.stateReason}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.5 }}>
                  {alarm.updatedAt ? new Date(alarm.updatedAt).toLocaleString() : '—'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </ChartCard>
    </Box>
  );
}
